import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  FormControl,
  FormLabel,
  Select,
  Textarea,
  useToast,
  Text,
  HStack,
  Icon,
  Spinner,
  Input,
  Alert,
  AlertIcon,
  AlertDescription,
  Box,
} from '@chakra-ui/react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db, app } from '../../../utils/init-firebase';
import { useAuth } from '../../../contexts/AuthContext';
import { FiMail, FiCopy, FiEye, FiClock } from 'react-icons/fi';
import { getFunctions, httpsCallable } from 'firebase/functions';
import moment from 'moment';
import RichTextEmailEditor from '../Email/RichTextEmailEditor';
import EmailPreviewModal from '../Email/EmailPreviewModal';
import AttachmentPicker from '../Email/AttachmentPicker';
import DynamicVariableInsertButton from '../Email/DynamicVariableInsertButton';
import { supabase, isSupabaseConfigured } from '../../../utils/supabase';
import { buildVariableMap, resolveTemplateVariables, fetchDealTeamVariableContext } from '../../../utils/templateVariables';

export default function EmailTemplateModal({ isOpen, onClose, deal, contact, replyToMessageId, references: threadReferences }) {
  const editorRef = React.useRef(null);
  const attachUploadRef = React.useRef(null);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [from, setFrom] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [userEmail, setUserEmail] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [showScheduler, setShowScheduler] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const draftTimerRef = useRef(null);
  const toast = useToast();
  const { currentUser, SuperAdmin, Roles } = useAuth();
  
  // Check if sending as someone else
  const isSendingAsOtherUser = from && userEmail && from.toLowerCase() !== userEmail.toLowerCase();
  const isCRMAdmin = SuperAdmin || Roles?.includes('CRM Admin');

  // Get current user's email on mount
  useEffect(() => {
    if (currentUser?.email) {
      setUserEmail(currentUser.email);
    }
  }, [currentUser]);

  // Prevent non-admins from sending as other users
  useEffect(() => {
    if (!isCRMAdmin && from && userEmail && from.toLowerCase() !== userEmail.toLowerCase()) {
      // Reset to user's own email if they try to change it
      setFrom(userEmail);
      toast({
        title: 'Permission required',
        description: 'Only admins can send emails as other users',
        status: 'warning',
        duration: 3000,
      });
    }
  }, [from, userEmail, isCRMAdmin, toast]);

  // Auto-update Reply-To when FROM changes (for admin sending as others)
  useEffect(() => {
    if (isOpen && from && isCRMAdmin) {
      // When FROM changes, update Reply-To to match (so replies go to loan officer)
      setReplyTo(from);
    }
  }, [from, isOpen, isCRMAdmin]);

  // Pre-populate FROM, REPLY-TO, and TO fields when modal opens
  useEffect(() => {
    if (isOpen) {
      // Set FROM to current user's email
      const userFromEmail = userEmail || currentUser?.email || '';
      setFrom(userFromEmail);
      
      // Set Reply-To to FROM by default (user can override)
      setReplyTo(userFromEmail);
      
      // Set TO to contact's email if available
      if (contact?.email) {
        setTo(contact.email);
      } else {
        setTo('');
      }
    }
  }, [isOpen, contact, userEmail, currentUser]);

  // Load email templates from Firestore
  useEffect(() => {
    const templatesRef = collection(db, 'emailTemplates');
    const q = query(templatesRef, where('isActive', '==', true));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const templatesList = [];
      snapshot.forEach((doc) => {
        templatesList.push({ id: doc.id, ...doc.data() });
      });
      setTemplates(templatesList);
      setLoadingTemplates(false);
    });

    return () => unsubscribe();
  }, []);

  // Draft feature: auto-save every 30 seconds.
  // Silently disabled if crm_email_drafts table doesn't exist yet (404).
  const draftsAvailable = useRef(true);

  const saveDraft = useCallback(async () => {
    if (!draftsAvailable.current || !isSupabaseConfigured() || !currentUser?.uid) return;
    if (!subject && !body) return;
    try {
      const draftData = {
        user_id: currentUser.uid,
        contact_id: contact?.id || null,
        deal_id: deal?.id || null,
        subject, body, to_email: to, cc, bcc,
        reply_to: replyTo, template_id: selectedTemplate || null,
        attachments: attachments.length > 0 ? attachments : [],
        updated_at: new Date().toISOString(),
      };
      if (draftId) {
        const { error } = await supabase.from('crm_email_drafts').update(draftData).eq('id', draftId);
        if (error?.code === '42P01' || error?.message?.includes('404')) { draftsAvailable.current = false; return; }
      } else {
        const { data, error } = await supabase.from('crm_email_drafts').insert(draftData).select('id').single();
        if (error?.code === '42P01' || error?.message?.includes('404')) { draftsAvailable.current = false; return; }
        if (data) setDraftId(data.id);
      }
      setDraftSavedAt(new Date());
    } catch (err) {
      // Silently ignore draft errors
    }
  }, [currentUser, contact, deal, subject, body, to, cc, bcc, replyTo, selectedTemplate, attachments, draftId]);

  useEffect(() => {
    if (!isOpen) return;
    if (draftTimerRef.current) clearInterval(draftTimerRef.current);
    draftTimerRef.current = setInterval(saveDraft, 30000);
    return () => clearInterval(draftTimerRef.current);
  }, [isOpen, saveDraft]);

  useEffect(() => {
    if (!isOpen || !isSupabaseConfigured() || !currentUser?.uid || !draftsAvailable.current) return;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('crm_email_drafts')
          .select('*')
          .eq('user_id', currentUser.uid)
          .eq('contact_id', contact?.id || '')
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (error?.code === '42P01' || error?.message?.includes('404')) { draftsAvailable.current = false; return; }
        if (data && data.body) {
          setDraftId(data.id);
          setSubject(data.subject || '');
          setBody(data.body || '');
          setTo(data.to_email || contact?.email || '');
          setCc(data.cc || '');
          setBcc(data.bcc || '');
          setAttachments(data.attachments || []);
        }
      } catch (err) {
        draftsAvailable.current = false;
      }
    })();
  }, [isOpen, currentUser, contact]);

  const deleteDraft = async () => {
    if (draftId && isSupabaseConfigured() && draftsAvailable.current) {
      try {
        await supabase.from('crm_email_drafts').delete().eq('id', draftId);
      } catch (err) { /* ignore */ }
      setDraftId(null);
      setDraftSavedAt(null);
    }
  };

  // Deal-team context for {{owner.*}} / role namespaces ({{csc.*}}, ...) so
  // template insertion previews resolve like the backend will at send time.
  const [dealTeam, setDealTeam] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const dealId = deal?.id || null;
    const dealOwnerId = deal?.ownerId || deal?.owner_id || null;
    const contactOwnerId = contact?.ownerId || contact?.owner_id || null;
    if (!dealId && !dealOwnerId && !contactOwnerId) {
      setDealTeam(null);
      return undefined;
    }
    fetchDealTeamVariableContext(dealId, { dealOwnerId, contactOwnerId })
      .then((ctx) => { if (!cancelled) setDealTeam(ctx); })
      .catch(() => { if (!cancelled) setDealTeam(null); });
    return () => { cancelled = true; };
  }, [deal?.id, deal?.ownerId, deal?.owner_id, contact?.ownerId, contact?.owner_id]);

  // Full variable map shared with the SMS compose surfaces. The backend
  // (sendEmailWithSignature → applyEmailTemplate) re-resolves everything at
  // send time with complete DB rows, so unknown tags are left visible here
  // instead of stripped — they still resolve correctly when the email sends.
  const variableMap = useMemo(() => buildVariableMap({
    contact,
    deal,
    sender: {
      name: currentUser?.displayName || currentUser?.email || '',
      email: currentUser?.email || '',
      phone: '',
    },
    dealTeam,
  }), [contact, deal, currentUser, dealTeam]);

  const replaceVariables = (text) => resolveTemplateVariables(text, variableMap, { stripUnknown: false });

  const handleTemplateChange = (templateId) => {
    setSelectedTemplate(templateId);
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSubject(replaceVariables(template.subject));
      setBody(replaceVariables(template.body));
    }
  };

  const handleCopyToClipboard = () => {
    let emailContent = `From: ${from}\n`;
    if (replyTo && replyTo !== from) emailContent += `Reply-To: ${replyTo}\n`;
    emailContent += `To: ${to}\n`;
    if (cc) emailContent += `CC: ${cc}\n`;
    if (bcc) emailContent += `BCC: ${bcc}\n`;
    emailContent += `Subject: ${subject}\n\n${body}`;
    
    navigator.clipboard.writeText(emailContent);
    toast({
      title: 'Copied to clipboard',
      status: 'success',
      duration: 2000,
    });
  };

  const handleSendEmail = async () => {
    if (!from || !from.trim()) {
      toast({
        title: 'Missing sender',
        description: 'Please specify a From email address',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    if (!to || !to.trim()) {
      toast({
        title: 'No recipient',
        description: 'Please enter at least one recipient email address',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    if (!subject || !body) {
      toast({
        title: 'Missing information',
        description: 'Please fill in subject and body',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Send email via Gmail API with signature
      const functions = getFunctions(app, 'us-east1');
      const sendEmailWithSignature = httpsCallable(functions, 'sendEmailWithSignature');
      
      const result = await sendEmailWithSignature({
        fromEmail: from.trim(),
        replyTo: replyTo.trim() || null,
        to: to.trim(),
        cc: cc.trim() || null,
        bcc: bcc.trim() || null,
        subject,
        body,
        contactId: contact?.id || null,
        dealId: deal?.id || null,
        attachments: attachments.length > 0 ? attachments : undefined,
        inReplyTo: replyToMessageId || undefined,
        references: threadReferences || undefined,
      });

      if (result.data.success) {
        toast({
          title: 'Email sent!',
          description: `Email sent successfully to ${to}`,
          status: 'success',
          duration: 4000,
        });

        await deleteDraft();

        setSelectedTemplate('');
        setFrom('');
        setReplyTo('');
        setTo('');
        setCc('');
        setBcc('');
        setSubject('');
        setBody('');
        setAttachments([]);
        onClose();
      } else {
        throw new Error(result.data.message || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      
      // Check for specific error types
      const errorMessage = error.message || 'Unknown error';
      
      if (errorMessage.includes('reconnect') || errorMessage.includes('insufficient authentication scopes')) {
        toast({
          title: 'Gmail not connected',
          description: 'Please connect your Gmail account in Settings first',
          status: 'warning',
          duration: 5000,
        });
      } else {
        toast({
          title: 'Error sending email',
          description: errorMessage,
          status: 'error',
          duration: 5000,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader>
          <HStack>
            <Icon as={FiMail} />
            <Text>Send Email</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody overflowY="auto">
          <VStack spacing={4} align="stretch">
            {/* Admin Warning: Sending as another user */}
            {isSendingAsOtherUser && isCRMAdmin && (
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <AlertDescription fontSize="sm">
                  <Text fontWeight="bold">Sending as another user</Text>
                  <Text>
                    Email will be sent from <strong>{from}</strong> with their signature.
                    Recipients will see this email came from {from}, not you.
                  </Text>
                </AlertDescription>
              </Alert>
            )}
            
            {/* Non-Admin Warning: Cannot send as others */}
            {isSendingAsOtherUser && !isCRMAdmin && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <AlertDescription fontSize="sm">
                  <Text fontWeight="bold">Permission Denied</Text>
                  <Text>
                    Only CRM Admins can send emails as other users.
                    The "From" field has been reset to your email.
                  </Text>
                </AlertDescription>
              </Alert>
            )}
            
            {/* From Field */}
            <FormControl isRequired>
              <FormLabel fontSize="sm">From</FormLabel>
              <Input
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                placeholder="your-email@example.com"
                size="sm"
                isReadOnly={!isCRMAdmin}
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                {isCRMAdmin 
                  ? "Email will be sent from this address with their Gmail signature."
                  : "You can only send from your own email address."}
              </Text>
            </FormControl>

            {/* Reply-To Field */}
            <FormControl>
              <FormLabel fontSize="sm">Reply-To (optional)</FormLabel>
              <Input
                value={replyTo}
                onChange={(e) => setReplyTo(e.target.value)}
                placeholder="reply-to@example.com"
                size="sm"
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                When recipient clicks Reply, this address will be used. Defaults to From address.
              </Text>
            </FormControl>

            {/* Email Recipients */}
            <FormControl isRequired>
              <FormLabel fontSize="sm">To</FormLabel>
              <Textarea
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="recipient@example.com, another@example.com"
                size="sm"
                rows={2}
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                {contact?.email 
                  ? `Pre-filled with: ${contact.email}` 
                  : 'Enter recipient email address(es), comma-separated'}
              </Text>
            </FormControl>

            <HStack spacing={3} align="flex-start">
              <FormControl flex={1}>
                <FormLabel fontSize="sm">CC (optional)</FormLabel>
                <Textarea
                  value={cc}
                  onChange={(e) => setCc(e.target.value)}
                  placeholder="cc@example.com"
                  size="sm"
                  rows={1}
                />
              </FormControl>

              <FormControl flex={1}>
                <FormLabel fontSize="sm">BCC (optional)</FormLabel>
                <Textarea
                  value={bcc}
                  onChange={(e) => setBcc(e.target.value)}
                  placeholder="bcc@example.com"
                  size="sm"
                  rows={1}
                />
              </FormControl>
            </HStack>

            <FormControl>
              <FormLabel fontSize="sm">Template</FormLabel>
              {loadingTemplates ? (
                <HStack p={2}>
                  <Spinner size="sm" />
                  <Text fontSize="sm">Loading templates...</Text>
                </HStack>
              ) : (
                <Select
                  placeholder="Select a template"
                  value={selectedTemplate}
                  onChange={(e) => handleTemplateChange(e.target.value)}
                  size="sm"
                >
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </Select>
              )}
              <Text fontSize="xs" color="gray.500" mt={1}>
                Variables: {'{{firstName}} {{lastName}} {{product}} {{amount}} {{lender}} {{loanNumber}} {{propertyAddress}} {{estimatedClosingDate}}'}
              </Text>
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Subject</FormLabel>
              <Textarea
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Email subject..."
                size="sm"
                rows={2}
              />
            </FormControl>

            <FormControl isRequired>
              <HStack justify="space-between" mb={1}>
                <FormLabel fontSize="sm" mb={0}>Body</FormLabel>
                <Button
                  size="xs"
                  variant="ghost"
                  leftIcon={<FiEye />}
                  onClick={() => setShowPreview(true)}
                >
                  Preview
                </Button>
              </HStack>
              <RichTextEmailEditor
                ref={editorRef}
                value={body}
                onChange={setBody}
                placeholder="Compose your email..."
                toolbarExtra={<DynamicVariableInsertButton editor={editorRef.current} />}
                onFileDrop={(file) => attachUploadRef.current?.(file)}
              />
            </FormControl>

            <AttachmentPicker attachments={attachments} onChange={setAttachments} uploadFileRef={attachUploadRef} />

            <EmailPreviewModal
              isOpen={showPreview}
              onClose={() => setShowPreview(false)}
              body={body}
              subject={subject}
              fromName={currentUser?.displayName || from || userEmail || 'You'}
              fromEmail={from || userEmail || ''}
              toLabel={to ? `to ${to}` : 'to me'}
              variables={{
                firstName: contact?.firstName || contact?.first_name || '',
                lastName: contact?.lastName || contact?.last_name || '',
                email: contact?.email || '',
                phone: contact?.phone || '',
                company: contact?.company || '',
                product: deal?.product || '',
                amount: deal?.amount ? `$${Number(deal.amount).toLocaleString()}` : '',
                dealName: deal?.dealName || deal?.deal_name || '',
                loanNumber: deal?.loanNumber || deal?.loan_number || '',
                lender: deal?.lender || '',
                loanPurpose: deal?.loanPurpose || deal?.loan_purpose || '',
                propertyAddress: [deal?.propertyAddress?.street, deal?.propertyAddress?.city, deal?.propertyAddress?.state, deal?.propertyAddress?.zip].filter(Boolean).join(', ') || '',
                estimatedClosingDate: deal?.estimatedClosingDate ? moment(deal.estimatedClosingDate).format('MMM DD, YYYY') : '',
                loanOfficerName: currentUser?.displayName || currentUser?.email || '',
                senderName: currentUser?.displayName || currentUser?.email || '',
                senderEmail: currentUser?.email || '',
              }}
            />
          </VStack>
        </ModalBody>

        <ModalFooter flexDir="column" alignItems="stretch" gap={3}>
          {showScheduler && (
            <Box borderWidth="1px" borderRadius="md" p={3} bg="gray.50">
              <HStack spacing={3} align="flex-end">
                <FormControl size="sm" flex={1}>
                  <FormLabel fontSize="xs" mb={1}>Date</FormLabel>
                  <Input
                    type="date"
                    size="sm"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    min={moment().format('YYYY-MM-DD')}
                  />
                </FormControl>
                <FormControl size="sm" flex={1}>
                  <FormLabel fontSize="xs" mb={1}>Time</FormLabel>
                  <Input
                    type="time"
                    size="sm"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                  />
                </FormControl>
                <Button
                  size="sm"
                  colorScheme="blue"
                  isLoading={isSubmitting}
                  isDisabled={!scheduledDate || !scheduledTime || !from || !to || !subject || !body}
                  onClick={async () => {
                    const scheduledAt = new Date(`${scheduledDate}T${scheduledTime}`);
                    if (isNaN(scheduledAt.getTime()) || scheduledAt <= new Date()) {
                      toast({ title: 'Pick a future date and time', status: 'warning', duration: 2000 });
                      return;
                    }
                    setIsSubmitting(true);
                    try {
                      const fns = getFunctions(app, 'us-east1');
                      const queueBulkEmails = httpsCallable(fns, 'queueBulkEmails');
                      await queueBulkEmails({
                        recipients: [contact?.id],
                        subject,
                        body,
                        fromEmail: from.trim(),
                        replyTo: replyTo.trim() || null,
                        attachments: attachments.length > 0 ? attachments : undefined,
                        scheduledAt: scheduledAt.toISOString(),
                      });
                      await deleteDraft();
                      toast({ title: 'Email scheduled', description: `Will send ${moment(scheduledAt).format('MMM D [at] h:mm A')}`, status: 'success', duration: 4000 });
                      setShowScheduler(false);
                      setScheduledDate('');
                      setScheduledTime('');
                      onClose();
                    } catch (err) {
                      toast({ title: 'Scheduling failed', description: err.message, status: 'error', duration: 4000 });
                    } finally {
                      setIsSubmitting(false);
                    }
                  }}
                >
                  Confirm Schedule
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setShowScheduler(false)}>
                  Cancel
                </Button>
              </HStack>
            </Box>
          )}

          <HStack w="100%">
            <HStack flex={1} spacing={2}>
              {draftSavedAt && (
                <Text fontSize="xs" color="gray.400">
                  Draft saved {moment(draftSavedAt).fromNow()}
                </Text>
              )}
            </HStack>
            <Button variant="ghost" mr={2} onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="outline"
              leftIcon={<FiCopy />}
              mr={2}
              onClick={handleCopyToClipboard}
              isDisabled={!subject || !body}
            >
              Copy
            </Button>
            <Button
              variant="outline"
              leftIcon={<FiClock />}
              mr={2}
              onClick={() => setShowScheduler(!showScheduler)}
              isDisabled={!from || !from.trim() || !to || !to.trim() || !subject || !body}
            >
              Schedule
            </Button>
            <Button
              colorScheme="blue"
              leftIcon={<FiMail />}
              onClick={handleSendEmail}
              isLoading={isSubmitting}
              loadingText="Sending..."
              isDisabled={!from || !from.trim() || !to || !to.trim() || !subject || !body}
            >
              Send Now
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

